import React, { useState } from 'react';
import UpsetPlot from './UpsetPlot';
import {
  Grid,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { countOrACVFormatter } from '../../util/customFunctions';
import { upsetData } from './Data';
import { C0002UpsetData } from './C0002UpsetData';

// Map company codes to their respective data files
const companyDataMap = {
  'C0002': C0002UpsetData,
  'C0022': upsetData,
  // Add more company mappings here as needed
  // 'C0003': C0003UpsetData,
  // 'C0004': C0004UpsetData,
};

function UpsetPlotPage({ company }) {
  console.log("Company:", company);
  
  // Get the appropriate data source based on company
  const currentUpsetData = companyDataMap[company] || upsetData;
  
  // Get the first available key as default (handles both "All Industries" and "All Categories")
  const defaultKey = Object.keys(currentUpsetData || {})[0] || 'All Industries';
  
  
  const [state, setState] = useState({
    industry: defaultKey,
    activeIndustry: defaultKey, // Track the industry/category currently displayed
  });

  // Get all available industries/categories from current company's data
  const getAvailableIndustries = () => {
    if (!currentUpsetData) return [defaultKey];
    
    const industries = Object.keys(currentUpsetData);
    
    // Separate "All Industries" or "All Categories" from the rest
    const allIndustries = industries.filter(ind => 
      ind === 'All Industries' || ind === 'All Categories'
    );
    const otherIndustries = industries
      .filter(ind => ind !== 'All Industries' && ind !== 'All Categories')
      .sort((a, b) => a.localeCompare(b)); // Alphabetical sort
    
    // Return with "All Industries/Categories" at the top
    return [...allIndustries, ...otherIndustries];
  };

  // Handle industry change (only updates dropdown, not the plot)
  const handleIndustryChange = (e) => {
    setState({
      ...state,
      industry: e.target.value,
    });
  };

  // Handle GO button click (updates the plot)
  const handleGo = () => {
    setState({
      ...state,
      activeIndustry: state.industry, // Update active industry to trigger plot update
    });
  };

  // Get data based on active industry from current company's data
  const rawData = currentUpsetData?.[state.activeIndustry];
  
  console.log("Active industry:", state.activeIndustry);
  console.log("Raw data:", rawData);
  
  // Check if data exists
  if (!rawData || !rawData.intersections) {
    return (
      <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
        <h1 style={{ textAlign: 'center', color: '#333' }}>
          No data available for company: {company} and industry: {state.activeIndustry}
        </h1>
        <Typography variant='body1' align='center' style={{ marginTop: '20px' }}>
          Available options: {getAvailableIndustries().join(', ')}
        </Typography>
      </div>
    );
  }
  
  const data = {
    ...rawData,
    intersections: rawData.intersections
      .sort((a, b) => b.size - a.size)
      .slice(0, 45)
  };

  // Optional: Custom configuration
  const config = {
    width: 1200,
    height: 600,
    margin: { top: 60, right: 40, bottom: 60, left: 200 },
    barChart: {
      height: 250,
      barPadding: 0.2
    },
    matrix: {
      height: 200,
      dotRadius: 5
    },
    setChart: {
      width: 150
    },
    colors: {
      bar: 'steelblue',
      barHover: '#4682b4',
      setBar: '#555',
      dot: '#4682b4',
      line: '#4472b4'
    }
  };

  return (
    <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
      {/* Filter Section */}
      <Grid container style={{ marginBottom: '20px' }}>
        {/* Industry Filter */}
        <Grid
          container
          item
          style={{ padding: 10, paddingTop: 15 }}
          xs={2}
          md={2}
        >
          <Grid item spacing={2} xs={12} container component={Paper}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel
                  id="select-industry-label"
                  style={{ 
                    color: state.industry !== defaultKey ? "#4472c4" : "#000" 
                  }}
                >
                  {company === 'C0002' ? 'Category:' : 'Industry:'}
                </InputLabel>
                <Select
                  data-test="industry"
                  labelId="select-industry-label"
                  id="industry"
                  value={state.industry}
                  onChange={handleIndustryChange}
                  label={company === 'C0002' ? 'Category' : 'Industry'}
                  name="industry"
                >
                  {getAvailableIndustries().map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry === 'All Industries' || industry === 'All Categories' ? 'All' : industry}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {/* GO Button */}
        <Grid
          container
          item
          style={{ padding: 10, paddingTop: 15 }}
          xs={12}
          md={2}
          alignItems="center"
        >
          <Grid item xs={12} container justify="flex-start">
            <Button
              data-test="go"
              variant="contained"
              color="primary"
              onClick={handleGo}
              style={{ height: '40px', marginTop: '8px' }}
            >
              GO
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {/* Title Section */}
      <h1 style={{ textAlign: 'center', color: '#333' }}>
        Product co-usage in the installed base ({state.activeIndustry})
      </h1>
      <Typography variant='h2' align='center'>
        Total {company === 'C0002' ? 'Items' : 'Accounts'}: {countOrACVFormatter(data.total_items || data.total_accounts)}
      </Typography>

      {/* UpSet Plot */}
      <UpsetPlot data={data} config={config} />
    </div>
  );
}

export default UpsetPlotPage;